/**
 * Voyage Radar vessel → GeoJSON Feature.
 *
 * Pure, so it can be unit-tested and reused by any map component. The host's
 * `convertToGeoJSONNew` delegates its VOYAGERADAR branch here; Spire and
 * ClydeSpace keep their own mappers.
 */

/** Property keys that survive from the raw feed onto the mapped Feature. */
interface OptionalField {
  /** Key on the raw upstream object. */
  from: string;
  /** Key on the mapped `properties` object. */
  to: string;
}

/**
 * Identity fields present only on live bbox features, plus the extras that only
 * history track-points (from `/v1/vessel/path`) carry. Copied when present so
 * neither shape silently loses data.
 */
const OPTIONAL_FIELDS: OptionalField[] = [
  { from: 'name', to: 'Name' },
  { from: 'shipType', to: 'Ship Type' },
  { from: 'flag', to: 'Flag' },
  { from: 'country', to: 'Country' },
  { from: 'navStatus', to: 'Nav Status' },
  { from: 'length', to: 'Length' },
  { from: 'beam', to: 'Beam' },
  { from: 'imo', to: 'IMO' },
  { from: 'callSign', to: 'Call Sign' },
  { from: 'color', to: 'Color' },
  { from: 'rate_of_turn', to: 'Rate of Turn' },
  { from: 'positionAccuracy', to: 'Position Accuracy' },
  { from: 'communicationState', to: 'Communication State' },
  { from: 'source', to: 'Source' },
];

/**
 * Voyage Radar arrives either as a GeoJSON FeatureCollection (the live bbox
 * pull) or as a flat TrackPoint array (history). Returns the vessel list from
 * whichever shape came in.
 */
export function toVoyageVesselList(vesselsData: any): any[] {
  const list = vesselsData?.features ?? vesselsData;
  return Array.isArray(list) ? list : [];
}

/**
 * Maps one vessel — a raw GeoJSON Feature or a flat TrackPoint — to the Feature
 * shape the map layers and the popup builder expect. Reads coordinates from
 * `geometry` when present and falls back to the flat lat/lng fields otherwise.
 */
export function toVoyageVesselFeature(vessel: any): any {
  const p = vessel?.properties || vessel || {};
  const geo = vessel?.geometry?.coordinates;
  const lng = geo ? geo[0] : p.longitude ?? p.Lng;
  const lat = geo ? geo[1] : p.latitude ?? p.Lat;
  const timestamp = p.time_utc ?? p.timestamp;

  const properties: any = {
    ID: `${p.mmsi}-${timestamp}`,
    MMSI: p.mmsi,
    'Timestamp UTC': timestamp,
    Lng: lng != null ? lng.toString() : '',
    Lat: lat != null ? lat.toString() : '',
    Course: p.cog,
    Speed: p.sog,
    Heading: p.heading,
  };
  for (const field of OPTIONAL_FIELDS) {
    if (p[field.from] != null) properties[field.to] = p[field.from];
  }

  return {
    type: 'Feature',
    id: `${p.mmsi}-${timestamp}`,
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties,
  };
}
